import React, { useState } from 'react';
import styled from 'styled-components';
import ImageModal from '../common/ImageModal';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
`;

const ModalContent = styled.div<{ $faction: 'human' | 'alien' }>`
  background: rgba(0, 25, 0, 0.95);
  border: 2px solid ${props => props.$faction === 'alien' ? '#ff8800' : '#3b82f6'};
  border-radius: 12px;
  padding: 30px;
  max-width: 500px;
  width: 90%;
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
`;

const CloseButton = styled.button<{ $faction: 'human' | 'alien' }>`
  background: none;
  border: 2px solid ${props => props.$faction === 'alien' ? '#ff8800' : '#3b82f6'};
  color: ${props => props.$faction === 'alien' ? '#ff8800' : '#3b82f6'};
  font-size: 20px;
  width: 40px;
  height: 40px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.$faction === 'alien' ? '#ff8800' : '#3b82f6'};
    color: ${props => props.$faction === 'alien' ? '#000' : '#000'};
  }
`;

const RankDisplay = styled.div`
  text-align: center;
  margin-bottom: 30px;
`;

const RankIcon = styled.div<{ $faction: 'human' | 'alien' }>`
  width: 120px;
  height: 120px;
  background: ${props => props.$faction === 'alien' ? '#ff8800' : '#3b82f6'};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$faction === 'alien' ? '#fff' : '#000'};
  font-weight: bold;
  font-size: 24px;
  margin: 0 auto 20px;
  border: 3px solid ${props => props.$faction === 'alien' ? '#ffaa00' : '#33d4ff'};
  box-shadow: 0 0 20px ${props => props.$faction === 'alien' ? 'rgba(255, 136, 0, 0.3)' : 'rgba(59, 130, 246, 0.3)'};
`;

const RankLevel = styled.div`
  color: #888;
  font-size: 14px;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const RankName = styled.h2<{ $faction: 'human' | 'alien' }>`
  color: ${props => props.$faction === 'alien' ? '#ff8800' : '#3b82f6'};
  font-size: 28px;
  font-weight: bold;
  margin: 0 0 20px 0;
  font-family: 'Courier New', monospace;
  text-transform: uppercase;
`;

const RankDetails = styled.div`
  display: grid;
  gap: 20px;
`;

const DetailSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 20px;
  border-radius: 8px;
  border-left: 4px solid ${props => props.color || '#3b82f6'};
`;

const DetailLabel = styled.div`
  color: #888;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
`;

const DetailValue = styled.div<{ $faction?: 'human' | 'alien' }>`
  color: ${props => 
    props.$faction === 'alien' ? '#ff8800' : 
    props.$faction === 'human' ? '#3b82f6' : '#fff'
  };
  font-size: 16px;
  font-weight: bold;
`;

const StatusBadge = styled.div<{ $status: 'achieved' | 'current' | 'locked'; $faction: 'human' | 'alien' }>`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
  
  ${props => {
    if (props.$status === 'achieved') {
      return `
        background: #4CAF50;
        color: #fff;
      `;
    } else if (props.$status === 'current') {
      return `
        background: ${props.$faction === 'alien' ? '#ff8800' : '#3b82f6'};
        color: ${props.$faction === 'alien' ? '#fff' : '#000'};
      `;
    } else {
      return `
        background: #666;
        color: #ccc;
      `;
    }
  }}
`;

const ProgressSection = styled.div<{ $faction: 'human' | 'alien' }>`
  margin-top: 20px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid ${props => props.$faction === 'alien' ? 'rgba(255, 136, 0, 0.3)' : 'rgba(59, 130, 246, 0.3)'};
`;

const ProgressLabel = styled.div`
  color: #888;
  font-size: 12px;
  text-transform: uppercase;
  margin-bottom: 10px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
`;

const ProgressFill = styled.div<{ $progress: number; $faction: 'human' | 'alien' }>`
  height: 100%;
  background: ${props => 
    props.$faction === 'alien' 
      ? 'linear-gradient(90deg, #ff8800, #cc6600)' 
      : 'linear-gradient(90deg, #3b82f6, #0099cc)'
  };
  width: ${props => props.$progress}%;
  transition: width 0.3s ease;
`;

const ProgressText = styled.div`
  color: #ccc;
  font-size: 12px;
  text-align: center;
`;

interface RankDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  rank: {
    level: number;
    name: string;
    pointsRequired: number;
    iconUrl?: string;
  } | null;
  faction: 'human' | 'alien';
  currentPoints: number;
}

const RankDetailModal: React.FC<RankDetailModalProps> = ({
  isOpen,
  onClose,
  rank,
  faction,
  currentPoints
}) => {
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  
  if (!isOpen || !rank) return null;

  const isAchieved = currentPoints >= rank.pointsRequired;
  const isCurrent = (() => {
    // Простая логика определения текущего ранга
    const nextRankPoints = Math.floor(100 * Math.pow(1.2, rank.level + 1));
    return currentPoints >= rank.pointsRequired && currentPoints < nextRankPoints;
  })();
  
  const status = isCurrent ? 'current' : isAchieved ? 'achieved' : 'locked';
  
  // Прогресс к этому рангу (если не достигнут)
  let progress = 0;
  let progressText = '';
  
  if (!isAchieved) {
    const prevRankPoints = rank.level === 0 ? 0 : Math.floor(100 * Math.pow(1.2, rank.level - 1));
    const pointsNeeded = rank.pointsRequired - prevRankPoints;
    const currentProgress = Math.max(0, currentPoints - prevRankPoints);
    progress = Math.min(100, (currentProgress / pointsNeeded) * 100);
    progressText = `${currentPoints}/${rank.pointsRequired} points`;
  } else {
    progress = 100;
    progressText = `Achieved with ${currentPoints} points`;
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleRankIconClick = () => {
    if (rank?.iconUrl) {
      setIsImageModalOpen(true);
    }
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContent $faction={faction}>
        <ModalHeader>
          <StatusBadge $status={status} $faction={faction}>
            {status}
          </StatusBadge>
          <CloseButton $faction={faction} onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <RankDisplay>
          <RankIcon 
            $faction={faction}
            onClick={handleRankIconClick}
            style={{ cursor: rank.iconUrl ? 'pointer' : 'default' }}
          >
            {rank.iconUrl ? (
              <img 
                src={rank.iconUrl} 
                alt={rank.name} 
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover', 
                  borderRadius: '8px' 
                }} 
              />
            ) : (
              rank.level.toString()
            )}
          </RankIcon>
          
          <RankLevel>Level {rank.level}</RankLevel>
          <RankName $faction={faction}>{rank.name}</RankName>
        </RankDisplay>

        <RankDetails>
          <DetailSection color={faction === 'alien' ? '#ff8800' : '#3b82f6'}>
            <DetailLabel>Points Required</DetailLabel>
            <DetailValue $faction={faction}>
              {rank.pointsRequired.toLocaleString()} points
            </DetailValue>
          </DetailSection>

          <DetailSection color={faction === 'alien' ? '#ff8800' : '#3b82f6'}>
            <DetailLabel>Faction</DetailLabel>
            <DetailValue $faction={faction}>
              {faction === 'human' ? 'Human Forces' : 'Alien Alliance'}
            </DetailValue>
          </DetailSection>

          {rank.level > 0 && (
            <DetailSection color="#4CAF50">
              <DetailLabel>Previous Rank Requirement</DetailLabel>
              <DetailValue>
                {Math.floor(100 * Math.pow(1.2, rank.level - 1)).toLocaleString()} points
              </DetailValue>
            </DetailSection>
          )}

          {rank.level < 49 && (
            <DetailSection color="#FF9800">
              <DetailLabel>Next Rank Requirement</DetailLabel>
              <DetailValue>
                {Math.floor(100 * Math.pow(1.2, rank.level + 1)).toLocaleString()} points
              </DetailValue>
            </DetailSection>
          )}
        </RankDetails>

        <ProgressSection $faction={faction}>
          <ProgressLabel>
            {isAchieved ? 'Achievement Progress' : 'Progress to this rank'}
          </ProgressLabel>
          <ProgressBar>
            <ProgressFill $progress={progress} $faction={faction} />
          </ProgressBar>
          <ProgressText>{progressText}</ProgressText>
        </ProgressSection>
      </ModalContent>
      
      <ImageModal
        isOpen={isImageModalOpen}
        imageUrl={rank?.iconUrl || ''}
        imageAlt={`${rank?.name || 'Rank'} icon`}
        title={`${rank?.name || 'Rank'} - Level ${rank?.level || 0}`}
        onClose={closeImageModal}
      />
    </ModalOverlay>
  );
};

export default RankDetailModal;